import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../helpers/api';
import { deletePartnerAd, getFilteredPartnerAds } from '../helpers/partnerAds';

const PartnerAdsList = () => {
  const [partnerAds, setPartnerAds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const location = useLocation();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10
  });

  useEffect(() => {
    if (location.state?.message) {
      const successMessage = document.getElementById('successMessage');
      if (successMessage) {
        successMessage.innerText = location.state.message;
        successMessage.style.display = 'block';
        setTimeout(() => {
          successMessage.style.display = 'none';
        }, 3000);
      }
    }
  }, [location]);

  useEffect(() => {
    fetchPartnerAds();
  }, [pagination.currentPage, searchTerm, activeFilter]);

  const fetchPartnerAds = async () => {
    try {
      setIsLoading(true);
      
      // Create filters object based on your current state
      const filters = {
        search: searchTerm
      };

      // Add active filter if selected
      if (activeFilter !== '') {
        filters.isActive = activeFilter === 'active';
      }

      // Call getFilteredPartnerAds with current pagination
      const { data, pagination: paginationData } = await getFilteredPartnerAds(
        filters,
        pagination.currentPage,
        pagination.pageSize
      );

      setPartnerAds(data);
      setPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(paginationData.total / pagination.pageSize)
      }));
    } catch (err) {
      setError('Erreur lors du chargement des publicités partenaires');
      console.error('Error fetching partner ads:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette publicité partenaire ?')) {
      return;
    }

    try {
      const response = await deletePartnerAd(id);
      if(response){
        setPartnerAds(prev => prev.filter(ad => ad.documentId !== id));
        // Afficher message de succès
        const successMessage = document.getElementById('successMessage');
        successMessage.innerText = 'Publicité partenaire supprimée avec succès';
        successMessage.style.display = 'block';
        setTimeout(() => {
          successMessage.style.display = 'none';
        }, 3000);
      }else{
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error('Error deleting partner ad:', err);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  if (isLoading && partnerAds.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Publicités Partenaires</h2>
        <Link to="/partner-ads/new" className="btn btn-primary">
          <i className="bi bi-plus-lg me-2"></i>
          Ajouter une publicité
        </Link>
      </div>

      <div className="alert alert-success" id="successMessage" style={{ display: 'none' }}></div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Rechercher une publicité..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <select 
            className="form-select" 
            value={activeFilter} 
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="bg-light">
              <tr>
                <th>Image</th>
                <th>Titre</th>
                <th>Sous-titre</th>
                <th>URL</th>
                <th>Statut</th>
                <th width="150">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partnerAds.map(ad => (
                <tr key={ad.id}>
                  <td className="align-middle">
                    {ad.image ? (
                      <img 
                        src={API_BASE_URL + ad.image.url} 
                        alt={ad.title} 
                        className="rounded"
                        style={{ width: '60px', height: '40px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div 
                        className="rounded bg-secondary" 
                        style={{ width: '60px', height: '40px' }}
                      ></div>
                    )}
                  </td>
                  <td className="align-middle">{ad.title}</td>
                  <td className="align-middle">{ad.subtitle}</td>
                  <td className="align-middle">
                    <a href={ad.url} target="_blank" rel="noopener noreferrer" className="text-truncate d-inline-block" style={{ maxWidth: '150px' }}>
                      {ad.url}
                    </a>
                  </td>
                  <td className="align-middle">
                    <span className={`badge ${ad.isActive ? 'bg-success' : 'bg-secondary'}`}>
                      {ad.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="align-middle">
                    <div className="btn-group">
                      <Link 
                        to={`/partner-ads/${ad.documentId}`}
                        className="btn btn-sm btn-outline-primary"
                        title="Éditer"
                      >
                        <i className="bi bi-pencil"></i>
                      </Link>
                      <button
                        onClick={() => handleDelete(ad.documentId)}
                        className="btn btn-sm btn-outline-danger"
                        title="Supprimer"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
            </li>
            
            {[...Array(pagination.totalPages)].map((_, index) => (
              <li 
                key={index + 1}
                className={`page-item ${pagination.currentPage === index + 1 ? 'active' : ''}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))}

            <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default PartnerAdsList;